// Account › Notifications › Desktop notifications: "Push to this browser" (HARDEN-pwa). On, the
// server sends this browser the inbox items whose Desktop column is on (outside quiet hours) as
// Web Push, so they arrive with CompanyOS closed; the in-page notifier still covers an open tab.
// The switch is off and says why when the server has no VAPID keys (`push.config` `reason`), the
// browser cannot receive push (iPhone/iPad: add to Home Screen first), or notifications are
// blocked. On/off is this browser's subscription as the server lists it (endpoint hashes).
import { SettingsRow, Switch, toast } from "@companyos/ui";
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import { browserPush, type PushBrowser, sha256Hex } from "../../pwa/push";

export const pushKeys = {
  config: (co: string) => ["company", co, "push-config"] as const,
  local: ["push-local-subscription"] as const,
};

export const pushConfigQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: pushKeys.config(co),
    queryFn: () => api.client.push.config({ params: { co } }),
  });

const IOS_HINT =
  "On iPhone and iPad, add CompanyOS to your Home Screen first (Share › Add to Home Screen), then turn this on there.";

export function PushToggle({
  api,
  co,
  browser = browserPush,
}: {
  api: Api;
  co: string;
  browser?: PushBrowser;
}) {
  const queryClient = useQueryClient();
  const config = useQuery(pushConfigQuery(api, co));
  const support = browser.support();
  const local = useQuery({
    queryKey: pushKeys.local,
    queryFn: async () => {
      const sub = await browser.current();
      return sub?.endpoint ? { endpoint: sub.endpoint, hash: await sha256Hex(sub.endpoint) } : null;
    },
    enabled: support === "supported" && config.data?.enabled === true,
  });
  const subscribed =
    Boolean(local.data) &&
    (config.data?.subscriptions ?? []).some((s) => s.endpoint_sha256 === local.data?.hash);

  const change = useMutation({
    mutationFn: async (on: boolean) => {
      if (on) {
        const key = config.data?.public_key;
        if (!key) throw new Error("Push is not set up on this server.");
        const sub = await browser.subscribe(key);
        if (!sub.endpoint || !sub.keys?.p256dh || !sub.keys.auth)
          throw new Error("The browser returned an incomplete subscription.");
        await api.client.push.subscribe({
          params: { co },
          body: {
            endpoint: sub.endpoint,
            expiration_time: sub.expirationTime ?? null,
            keys: { p256dh: sub.keys.p256dh, auth: sub.keys.auth },
          },
        });
      } else {
        const endpoint = local.data?.endpoint ?? (await browser.unsubscribe());
        if (endpoint) await api.client.push.unsubscribe({ params: { co }, body: { endpoint } });
      }
    },
    onSuccess: (_d, on) =>
      toast.add({ title: on ? "Push is on for this browser" : "Push is off for this browser" }),
    onError: (e) =>
      toast.add({
        title: "Push not changed",
        description: e instanceof Error ? e.message : String(e),
        type: "error",
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: pushKeys.config(co) });
      void queryClient.invalidateQueries({ queryKey: pushKeys.local });
    },
  });

  const permission = browser.permission();
  const reason = config.isPending
    ? "Checking…"
    : config.isError
      ? "Could not read the push settings."
      : !config.data.enabled
        ? (config.data.reason ?? "Push is not set up on this server.")
        : support === "ios-install-first"
          ? IOS_HINT
          : support === "unsupported"
            ? "This browser cannot receive push notifications."
            : permission === "denied"
              ? "Notifications are blocked for this site. Allow them in the browser's settings."
              : null;

  return (
    <SettingsRow
      label="Push to this browser"
      hint={
        reason ??
        "Items with Desktop on arrive even when CompanyOS is closed (not during quiet hours)."
      }
      htmlFor="push-browser"
    >
      <Switch
        id="push-browser"
        aria-label="Push to this browser"
        checked={reason === null && subscribed}
        disabled={reason !== null || change.isPending || local.isPending}
        onCheckedChange={(v) => change.mutate(Boolean(v))}
      />
    </SettingsRow>
  );
}
